// Adding an authenticator app needs proof that the owner is present
// (SR-2026-10-28-02). Right after a sign-in with a code that proof is the
// sign-in itself; otherwise the server answers 403 and the person confirms
// with their password and a code by SMS (a "totp_enrol" step-up).
import { Button, PasswordInput, Stack, Text } from "@mantine/core";
import { useCallback, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { errorMessage } from "./errors";
import { useStepUp } from "./stepup";

export type Enrolment = { secret: string; otpauth_url: string };

type Begun = { enrolment?: Enrolment; error?: string };

/** useEnrolment starts adding an authenticator app, asking for proof when the server needs it. */
export function useEnrolment() {
  const { t } = useTranslation();
  const stepUp = useStepUp();
  const [needsProof, setNeedsProof] = useState(false);

  const begin = useCallback(
    async (password?: string): Promise<Begun> => {
      if (password === undefined) {
        const { data, error, response } = await api.POST("/mfa/totp");
        if (data) return { enrolment: data };
        if (response.status === 403) {
          setNeedsProof(true);
          return {};
        }
        return { error: errorMessage(error, t("error.generic")) };
      }
      const token = await stepUp("totp_enrol");
      if (!token) return {};
      const { data, error } = await api.POST("/mfa/totp", {
        params: { header: { "X-Step-Up-Token": token } },
        body: { password },
      });
      if (!data) return { error: errorMessage(error, t("error.generic")) };
      setNeedsProof(false);
      return { enrolment: data };
    },
    [stepUp, t],
  );

  return { needsProof, begin };
}

/** ProofForm asks for the password; the SMS code follows in the step-up dialog. */
export function ProofForm({ busy, onSubmit }: { busy: boolean; onSubmit: (password: string) => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password) onSubmit(password);
  };
  return (
    <form onSubmit={submit} data-testid="enrol-proof">
      <Stack gap="sm">
        <Text size="sm" fw={600}>
          {t("enrol_proof.title")}
        </Text>
        <Text size="sm">{t("enrol_proof.body")}</Text>
        <PasswordInput
          label={t("enrol_proof.password")}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          required
          maw={360}
        />
        <Button type="submit" w="fit-content" loading={busy} disabled={!password}>
          {t("enrol_proof.continue")}
        </Button>
      </Stack>
    </form>
  );
}
